import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon'
import { faPowerOff } from '@fortawesome/free-solid-svg-icons/faPowerOff'
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt'
import { faBatteryFull } from '@fortawesome/free-solid-svg-icons/faBatteryFull'
import { faPlug } from '@fortawesome/free-solid-svg-icons/faPlug'
import { useSnapshot } from 'valtio'
import { dayHistoryState } from '../../../../store/dayHistoryState.ts'
import { authState, effectiveTimezone } from '../../../../store/authState.ts'
import { getProgress } from '../../../../helpers/dayHistoryHelper.ts'
import { IDayHistoryGap, IDayHistoryEvent } from '../../../../interfaces/dayHistory.ts'

const EVENT_ICONS: Record<string, any> = {
    outOfCharger: faPlug,
    inCharger: faPlug,
    powerUp: faBolt,
    powerDown: faPowerOff,
    fullCharge: faBatteryFull,
    wakeResponse: faMoon,
    sleepResponse: faMoon,
    beaconInRange: faBolt,
    beaconOutOfRange: faBolt,
}

const EVENT_LABELS: Record<string, string> = {
    outOfCharger: 'Out of charger',
    inCharger: 'In charger',
    powerUp: 'Power on',
    powerDown: 'Power off',
    fullCharge: 'Full charge',
    wakeResponse: 'Wake',
    sleepResponse: 'Sleep',
    beaconInRange: 'Power save on',
    beaconOutOfRange: 'Power save off',
}

/**
 * Timeline scrubber bar for Day History.
 * Shows:
 * - A full-width bar representing the day's duration
 * - Gray/dimmed regions for gaps
 * - Small event icons at their time positions
 * - A draggable playhead showing current progress
 */
export const DayHistoryTimeline: React.FC = () => {
    const snap = useSnapshot(dayHistoryState) as typeof dayHistoryState
    const pathData = snap.pathData
    const currentTime = snap.currentTime
    const isPlaying = snap.isPlaying
    const selectedDate = snap.selectedDate
    useSnapshot(authState)
    const zone = effectiveTimezone().value

    const barRef = React.useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = React.useState(false)

    if (!pathData) return null

    const { duration, gaps, events, path } = pathData
    const totalDuration = duration.end - duration.start
    if (totalDuration <= 0) return null

    // No reports for the day — render a fully gray bar
    const noReports = path.length === 0

    const progress = getProgress(currentTime, duration.start, duration.end)

    // Convert a time to a percentage position on the bar
    const timeToPercent = (time: number) => {
        return ((time - duration.start) / totalDuration) * 100
    }

    // Detect "today" to show the future portion differently
    const now = DateTime.now().setZone(zone)
    const isToday = selectedDate ? selectedDate.hasSame(now, 'day') : false
    const nowEpoch = Math.floor(now.toSeconds())
    // Future region: from current real time to the end of the timeline (only for today)
    const futureStartPercent = isToday && nowEpoch > duration.start && nowEpoch < duration.end
        ? timeToPercent(nowEpoch)
        : null

    // Handle click/drag on the timeline bar
    const scrubToPosition = (clientX: number) => {
        if (!barRef.current) return
        const rect = barRef.current.getBoundingClientRect()
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
        const percent = x / rect.width
        let newTime = duration.start + percent * totalDuration

        // Clamp to current time when viewing today
        if (futureStartPercent !== null) {
            newTime = Math.min(newTime, nowEpoch)
        }

        dayHistoryState.currentTime = newTime
    }

    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault()
        setIsDragging(true)
        // Pause during scrub
        if (isPlaying) {
            dayHistoryState.isPlaying = false
        }
        scrubToPosition(e.clientX)
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return
        scrubToPosition(e.clientX)
    }

    const handlePointerUp = (e: React.PointerEvent) => {
        if (!isDragging) return
        setIsDragging(false)
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    }

    // Gap regions (rendered as dimmed overlay sections)
    // When viewing today, clip gaps so they don't extend into the future region
    const gapRegions = gaps.map((gap: IDayHistoryGap, i: number) => {
        const effectiveEnd = futureStartPercent !== null
            ? Math.min(gap.endTime, nowEpoch)
            : gap.endTime
        // Skip gaps entirely in the future
        if (futureStartPercent !== null && gap.startTime >= nowEpoch) return null

        const left = timeToPercent(gap.startTime)
        const width = timeToPercent(effectiveEnd) - left
        if (width <= 0) return null

        const gapStart = DateTime.fromSeconds(gap.startTime).setZone(zone).toFormat('h:mm a')
        const gapEnd = DateTime.fromSeconds(effectiveEnd).setZone(zone).toFormat('h:mm a')
        const reasonLabel = gap.reason === 'no_data'
            ? 'No reports received'
            : gap.reason === 'sleeping'
                ? 'Sleeping'
                : gap.reason === 'powered_off'
                    ? 'Powered off'
                    : gap.reason === 'invalid_gps'
                        ? 'Weak GPS signal'
                        : gap.reason === 'charging'
                            ? 'In charger'
                            : gap.reason
        return (
            <div
                key={`gap-${i}`}
                className={'absolute top-0 bottom-0 bg-gray-400/40 rounded-sm'}
                style={{ left: `${left}%`, width: `${width}%` }}
                title={`${reasonLabel} · ${gapStart} – ${gapEnd}`}
            />
        )
    })

    // Event markers (small icons positioned along the bar)
    const eventMarkers = events.map((event: IDayHistoryEvent, i: number) => {
        const left = timeToPercent(event.time)
        const icon = EVENT_ICONS[event.type]
        if (!icon) return null
        const timeLabel = DateTime.fromSeconds(event.time).setZone(zone).toFormat('h:mm a')
        const eventLabel = EVENT_LABELS[event.type] || event.type
        return (
            <div
                key={`event-${i}`}
                className={'tooltip tooltip-top absolute -top-3 -translate-x-1/2 text-secondary opacity-70'}
                style={{ left: `${left}%` }}
                data-tip={`${eventLabel} · ${timeLabel}`}
            >
                <FontAwesomeIcon icon={icon} size={'xs'} />
            </div>
        )
    })

    return (
        <div className={'w-full flex flex-col gap-1'}>
            {/* Label */}
            <div className={'text-xs font-semibold text-primary px-1'}>
                {noReports ? 'No reports received this day' : 'Timeline — drag to jump to any point in the day'}
            </div>
            {/* Timeline bar */}
            <div
                ref={barRef}
                className={`relative w-full h-3 rounded-full touch-none select-none ${noReports ? 'bg-gray-300' : 'bg-primary/20 cursor-pointer'}`}
                onPointerDown={noReports ? undefined : handlePointerDown}
                onPointerMove={noReports ? undefined : handlePointerMove}
                onPointerUp={noReports ? undefined : handlePointerUp}
                onPointerCancel={noReports ? undefined : handlePointerUp}
                title={noReports ? 'No reports received' : undefined}
            >
                {!noReports && (
                    <>
                        {/* Gap regions */}
                        {gapRegions}

                        {/* Future region for today — dashed outline, very light gray fill */}
                        {futureStartPercent !== null && (
                            <div
                                className={'absolute top-0 bottom-0 rounded-r-full border border-dashed border-gray-400 bg-gray-100/50 z-[3] cursor-default'}
                                style={{ left: `${futureStartPercent}%`, width: `${100 - futureStartPercent}%` }}
                                title={'Future — not yet reached'}
                            />
                        )}

                        {/* Progress fill — above gaps so traveled portion shows blue */}
                        <div
                            className={'absolute top-0 left-0 bottom-0 bg-primary rounded-full pointer-events-none z-[1]'}
                            style={{ width: `${progress * 100}%` }}
                        />

                        {/* Playhead */}
                        <div
                            className={'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-primary border-2 border-white rounded-full shadow pointer-events-none z-[2]'}
                            style={{ left: `${progress * 100}%` }}
                        />

                        {/* Event markers above the bar */}
                        {eventMarkers}
                    </>
                )}
            </div>

            {/* Time labels */}
            <div className={'flex justify-between text-xs text-gray-500 px-1'}>
                <span>{DateTime.fromSeconds(duration.start).setZone(zone).toFormat('ha').toLowerCase()}</span>
                {!noReports && <span className={'text-base-content/40 italic'}>drag to scrub</span>}
                <span>{DateTime.fromSeconds(duration.end).setZone(zone).toFormat('ha').toLowerCase()}</span>
            </div>
        </div>
    )
}
